import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle2, Phone, Calendar, Home, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const Merci = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Déclencher l'événement de conversion Google Ads
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-18129697576/4ciGCPK4v6UcEKj29MRD',
        'value': 1.0,
        'currency': 'CHF'
      });
    }
  }, []);
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Icône de succès */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 bg-green-100 rounded-full mb-6">
              <CheckCircle2 className="w-12 h-12 sm:w-14 sm:h-14 text-green-600" />
            </div>
            
            <h1 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2B2B2B] mb-4"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Merci pour votre confiance !
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-600 mb-8">
              Votre demande a bien été réceptionnée.
            </p>
          </div>
          
          {/* Card principale */}
          <Card className="p-6 sm:p-8 mb-8 shadow-xl border-green-200 bg-white">
            <div className="space-y-6">
              {/* Étape 1 : Appel */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#336DBF] rounded-full flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-[#2B2B2B] mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Étape 1 : Appel de confirmation
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    <strong className="text-[#336DBF]">Un agent va vous appeler dans les prochaines minutes</strong> pour confirmer votre demande et répondre à vos premières questions.
                  </p>
                </div>
              </div>
              
              <div className="border-t border-slate-200"></div>
              
              {/* Étape 2 : RDV */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#336DBF] rounded-full flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-[#2B2B2B] mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Étape 2 : Prise de rendez-vous
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    Lors de cet appel, nous fixerons ensemble un rendez-vous avec <strong>notre technicien qualifié</strong> qui viendra sur place.
                  </p>
                </div>
              </div>
              
              <div className="border-t border-slate-200"></div>
              
              {/* Étape 3 : Visite */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#336DBF] rounded-full flex items-center justify-center">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-[#2B2B2B] mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Étape 3 : Visite technique
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    Notre technicien viendra <strong>évaluer votre projet sur place</strong> et vous conseiller sur les travaux à effectuer.
                  </p>
                </div>
              </div>
            </div>
          </Card>
          
          {/* Message d'accueil */}
          <Card className="p-6 sm:p-8 mb-8 bg-gradient-to-r from-[#336DBF] to-[#2F62AA] text-white border-none">
            <p className="text-center text-lg sm:text-xl font-medium leading-relaxed">
              <strong className="text-2xl sm:text-3xl block mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Merci de nous faire bon accueil ! 🏠
              </strong>
              Nous sommes impatients de transformer votre salle de bains en un espace moderne et fonctionnel.
            </p>
          </Card>
          
          {/* Informations pratiques */}
          <Card className="p-6 sm:p-8 mb-8 bg-slate-50 border-slate-200">
            <h3 className="text-lg font-bold text-[#2B2B2B] mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              En attendant notre appel :
            </h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Gardez votre téléphone à portée de main</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Préparez vos disponibilités pour le rendez-vous technique</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Notez vos questions éventuelles</span>
              </li>
            </ul>
          </Card>
          
          {/* Bouton retour */}
          <div className="text-center">
            <Button
              onClick={() => navigate('/')}
              size="lg"
              variant="outline"
              className="border-2 border-[#336DBF] text-[#336DBF] hover:bg-[#336DBF] hover:text-white font-medium px-8 py-6"
            >
              Retour à l'accueil
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
          
          {/* Note réassurance */}
          <p className="text-center text-sm text-slate-500 mt-8">
            Vous n'avez pas reçu d'appel ? Vérifiez vos spams ou contactez-nous au <strong className="text-[#336DBF]">+41 24 539 10 33</strong>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Merci;
